import { console_api } from '../http/consoleClient';
import { toBase64 } from '../encoding';
import {
  ColumnsDerivedGenerationResult,
  ConsoleDashboardDetail,
  ConsoleDashboardFactColumnsResponse,
  ConsoleDashboardModelRow,
  ConsoleDashboardSummary,
  ConsoleDashboardTemporalDeriveResult,
  DashboardSqlValidationResult,
} from 'types/console/dashboards.types';

export const consoleDashboardsApi = {
  listDashboards: async (requesterEmail: string, includeDeleted = false): Promise<ConsoleDashboardSummary[]> => {
    const response = await console_api.get<{ dashboards: ConsoleDashboardSummary[] }>('/console/list_dashboards', {
      params: { requester_email: requesterEmail, include_deleted: includeDeleted },
    });
    return response.data.dashboards;
  },

  upsertAdminDashboard: async (payload: {
    requester_email: string;
    dashboard_id?: string;
    name: string;
    description: string;
    primary_developer_email: string;
    secondary_developer_email: string;
    authorized_users_manager_email: string;
    source_link?: string;
  }): Promise<ConsoleDashboardSummary> => {
    const response = await console_api.post<ConsoleDashboardSummary>('/console/admin/upsert_dashboard', payload);
    return response.data;
  },

  getDashboardDetail: async (dashboardId: string, requesterEmail: string): Promise<ConsoleDashboardDetail> => {
    const response = await console_api.get<ConsoleDashboardDetail>(
      `/console/get_dashboard/${encodeURIComponent(dashboardId)}`,
      { params: { requester_email: requesterEmail } }
    );
    return response.data;
  },

  updateDashboardDescriptionSections: async (payload: {
    requester_email: string;
    dashboard_id: string;
    dashboard_purpose: string;
    cannot_answer: string;
    filters_applicable: string;
    what_it_answers: string;
    business_rules_logic: string;
  }): Promise<void> => {
    await console_api.patch(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/description-sections`,
      {
        requester_email: payload.requester_email,
        dashboard_purpose: payload.dashboard_purpose,
        cannot_answer: payload.cannot_answer,
        filters_applicable: payload.filters_applicable,
        what_it_answers: payload.what_it_answers,
        business_rules_logic: payload.business_rules_logic,
      }
    );
  },

  // Called once per model row — index 0 = fact, index 1+ = bridge
  // Returns is_validated and execution_error from the API response
  updateDashboardModelRow: async (payload: {
    requester_email: string;
    dashboard_id: string;
    index: number;
    role: 'fact' | 'bridge';
    view_name: string;
    grain: string;
    alias: string;
    primary_key: string;
    sql_view: string;
    source_tables: Array<{ table_id: number; table_name: string }>;
    join_to_fact: { fact_key: string; this_key: string; type: string } | null;
  }): Promise<{
    dashboard_id: string;
    index: number;
    model_row: ConsoleDashboardModelRow;
    is_validated: boolean;
    updated_at: string;
    updated_by: string;
    execution_error?: string | null;
  }> => {
    const response = await console_api.put<{
      dashboard_id: string;
      index: number;
      model_row: ConsoleDashboardModelRow;
      is_validated: boolean;
      updated_at: string;
      updated_by: string;
      execution_error?: string | null;
    }>(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/model`,
      {
        requester_email: payload.requester_email,
        index: payload.index,
        role: payload.role,
        view_name: payload.view_name,
        grain: payload.grain,
        alias: payload.alias,
        primary_key: payload.primary_key,
        sql_view: payload.sql_view,
        source_tables: payload.source_tables,
        join_to_fact: payload.join_to_fact,
      }
    );
    return response.data;
  },

  deleteDashboardModelRow: async (payload: {
    requester_email: string;
    dashboard_id: string;
    index: number;
    view_name: string;
    role: string;
  }): Promise<void> => {
    await console_api.request({
      method: 'DELETE',
      url: `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/model`,
      data: {
        requester_email: payload.requester_email,
        index: payload.index,
        view_name: payload.view_name,
        role: payload.role,
      },
    });
  },

  updateDashboardColumnsDerived: async (payload: {
    requester_email: string;
    dashboard_id: string;
    views: Array<{
      view_name: string;
      tables: Array<{ table_name: string; columns: string[] }>;
    }>;
  }): Promise<void> => {
    await console_api.put(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/columns-derived`,
      {
        requester_email: payload.requester_email,
        views: payload.views,
      }
    );
  },

  // Generates columns derived for a single view (fact or bridge) by view_name
  generateDashboardColumnsDerived: async (payload: {
    requester_email: string;
    dashboard_id: string;
    view_name: string;
  }): Promise<ColumnsDerivedGenerationResult> => {
    const response = await console_api.post<ColumnsDerivedGenerationResult>(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/columns-derived-generation`,
      {
        requester_email: payload.requester_email,
        view_name: payload.view_name,
      }
    );
    return response.data;
  },

  // Saves a description for a single manual construction view_column entry
  updateManualConstructionDescription: async (payload: {
    requester_email: string;
    dashboard_id: string;
    view_name: string;
    view_column: string;
    description: string;
  }): Promise<void> => {
    await console_api.patch(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/columns-derived/manual-construction`,
      {
        requester_email: payload.requester_email,
        view_name: payload.view_name,
        view_column: payload.view_column,
        description: payload.description,
      }
    );
  },

  indexDashboardNow: async (payload: {
    requester_email: string;
    dashboard_id: string;
  }): Promise<void> => {
    await console_api.post(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/index-now`,
      { requester_email: payload.requester_email }
    );
  },

  updateDashboardAuthorizedUsers: async (payload: {
    requester_email: string;
    dashboard_id: string;
    authorized_users: string[];
  }): Promise<void> => {
    await console_api.put(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/authorized-users`,
      {
        requester_email: payload.requester_email,
        authorized_users: payload.authorized_users,
      }
    );
  },

  getDashboardFactColumns: async (dashboardId: string, requesterEmail: string): Promise<string[]> => {
    const response = await console_api.get<ConsoleDashboardFactColumnsResponse>(
      `/console/dashboards/${encodeURIComponent(dashboardId)}/fact-columns`,
      { params: { requester_email: requesterEmail } }
    );
    return response.data.columns || [];
  },

  deriveDashboardTemporalScope: async (payload: {
    requester_email: string;
    dashboard_id: string;
    column_name: string;
  }): Promise<ConsoleDashboardTemporalDeriveResult> => {
    const response = await console_api.post<ConsoleDashboardTemporalDeriveResult>(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/temporal-scope`,
      {
        requester_email: payload.requester_email,
        column_name: payload.column_name,
      }
    );
    return response.data;
  },

  pushDashboardToProd: async (payload: {
    requester_email: string;
    dashboard_id: string;
    enabled: boolean;
  }): Promise<{ dashboard_id: string; prod_enabled: boolean; jobs_updated: number }> => {
    const response = await console_api.patch<{ dashboard_id: string; prod_enabled: boolean; jobs_updated: number }>(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/prod-push`,
      {
        requester_email: payload.requester_email,
        enabled: payload.enabled,
      }
    );
    return response.data;
  },

  approveDashboard: async (payload: {
    requester_email: string;
    dashboard_id: string;
    stage: 'developer' | 'admin';
    is_approved: boolean;
    note?: string;
  }): Promise<ConsoleDashboardSummary> => {
    const response = await console_api.patch<ConsoleDashboardSummary>(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/approval`,
      {
        requester_email: payload.requester_email,
        stage: payload.stage,
        is_approved: payload.is_approved,
        note: payload.note || '',
      }
    );
    return response.data;
  },

  setDashboardSoftDelete: async (payload: {
    requester_email: string;
    dashboard_id: string;
    is_deleted: boolean;
  }): Promise<ConsoleDashboardSummary> => {
    const response = await console_api.patch<ConsoleDashboardSummary>(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/soft-delete`,
      {
        requester_email: payload.requester_email,
        is_deleted: payload.is_deleted,
      }
    );
    return response.data;
  },

  syncDashboardAccess: async (payload: {
    requester_email: string;
    dashboard_id: string;
  }): Promise<void> => {
    await console_api.post(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/access-sync`,
      { requester_email: payload.requester_email }
    );
  },

  validateDashboardSql: async (payload: {
    requester_email: string;
    dashboard_id: string;
    sql: string;
  }): Promise<DashboardSqlValidationResult> => {
    const response = await console_api.post<DashboardSqlValidationResult>(
      `/console/dashboards/${encodeURIComponent(payload.dashboard_id)}/validate-sql`,
      {
        requester_email: payload.requester_email,
        sql_base64: toBase64(payload.sql),
      }
    );
    return response.data;
  },
};
